import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import supabase from '../config/supabaseClient';
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCheck, FaTimes, FaMobileAlt } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SubscriptionSection from './SubscriptionSection';
import UpgradeModal from './UpgradeModal';
import './Profile.css';

// Brand colors from the logo
const brandColors = {
  primary: '#29458d',    // Dark blue
  secondary: '#dbc078',  // Gold
  white: '#ffffff',
  lightBlue: '#e8edf7'   // Light version of primary for backgrounds
};

function Profile() {
  const { user } = useAuth();
  const [upgradeModal, setUpgradeModal] = useState({ open: false });
  const profileRefreshGuardRef = useRef({ inFlight: false, lastAt: 0 });
  const TWILIO_SUBSCRIBE_NUMBER = (process.env.REACT_APP_TWILIO_SUBSCRIBE_NUMBER || '+18338475141').trim();
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    newFirstName: '',
    newLastName: '',
    newEmail: ''
  });

  const [smsProfile, setSmsProfile] = useState({
    phoneNumber: '',
    smsOptIn: false
  });

  // If the user texted STOP, carriers/Twilio will block future messages until they text START.
  // We store this so the UI doesn't mislead users into thinking website opt-in will work.
  const [smsOptOutSource, setSmsOptOutSource] = useState('');

  const [smsSettings, setSmsSettings] = useState({
    phoneInput: '',
    phoneCountry: 'US', // UI-only in production (locked), selectable in dev/preview
    smsOptIn: false,
    termsAccepted: false
  });

  const [smsPhoneError, setSmsPhoneError] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);
  const [marginTop, setMarginTop] = useState('0px');  // Default margin for desktop

  // When the user arrives here from the pricing page's "Start free trial" CTA without
  // a phone number, scroll to the SMS section and show a banner explaining why.
  const location = useLocation();
  const fromPricingTrial = location?.state?.fromPricingTrial === true;
  const [bannerDismissed, setBannerDismissed] = useState(false);
  useEffect(() => {
    if (location?.hash === '#sms-notifications') {
      // Defer to next tick so the SMS section has rendered before we scroll.
      setTimeout(() => {
        const el = document.getElementById('sms-notifications');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [location?.hash]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Password states
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  // Add responsive margin adjustment
  useEffect(() => {
    const handleResize = () => {
      setMarginTop(window.innerWidth <= 768 ? '0px' : '0px');
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch user profile data on mount
  const fetchUserProfile = useCallback(async () => {
    try {
      if (!user) {
        setError('No user session found');
        return;
      }

      // Guard: coming back to the tab can fire multiple events (Supabase auth refresh + visibility/focus).
      // Prevent spammy re-fetches.
      const now = Date.now();
      if (profileRefreshGuardRef.current.inFlight) return;
      if (now - (profileRefreshGuardRef.current.lastAt || 0) < 1500) return;
      profileRefreshGuardRef.current.inFlight = true;

      // Get the user metadata first
      const { data: userData } = await supabase.auth.getUser();
      const metadata = userData?.user?.user_metadata || {};

      // Try to get existing profile with a single query
      const { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) {
        console.error('Profile fetch error:', fetchError);
        throw fetchError;
      }

      if (!existingProfile) {
        try {
          // Profile doesn't exist, create it
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              user_id: user.id,
              first_name: metadata.first_name || '',
              last_name: metadata.last_name || '',
              updated_at: new Date()
            })
            .select()
            .single();

          if (insertError) {
            // If insert fails due to race condition, try one final fetch
            if (insertError.code === '23505') {
              const { data: retryProfile, error: retryError } = await supabase
                .from('profiles')
                .select('first_name, last_name')
                .eq('user_id', user.id)
                .single();

              if (retryError) {
                console.error('Final retry error:', retryError);
                throw retryError;
              }

              setProfileData(prev => ({
                ...prev,
                firstName: retryProfile.first_name || '',
                lastName: retryProfile.last_name || ''
              }));

              setSmsProfile({
                phoneNumber: '',
                smsOptIn: false
              });
              setSmsSettings({
                phoneInput: '',
                smsOptIn: false,
                termsAccepted: false
              });
              return;
            }
            throw insertError;
          }

          setProfileData(prev => ({
            ...prev,
            firstName: metadata.first_name || '',
            lastName: metadata.last_name || ''
          }));

          setSmsProfile({
            phoneNumber: '',
            smsOptIn: false
          });
          setSmsSettings({
            phoneInput: '',
            smsOptIn: false,
            termsAccepted: false
          });
          return;
        } catch (insertError) {
          if (insertError.code === '23505') {
            // One final attempt to fetch if insert failed
            const { data: finalProfile, error: finalError } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('user_id', user.id)
              .single();

            if (!finalError && finalProfile) {
              setProfileData(prev => ({
                ...prev,
                firstName: finalProfile.first_name || '',
                lastName: finalProfile.last_name || ''
              }));

              setSmsProfile({
                phoneNumber: '',
                smsOptIn: false
              });
              setSmsSettings({
                phoneInput: '',
                smsOptIn: false,
                termsAccepted: false
              });
              return;
            }
          }
          throw insertError;
        }
      }

      // Profile exists, use its data
      setProfileData(prev => ({
        ...prev,
        firstName: existingProfile.first_name || '',
        lastName: existingProfile.last_name || ''
      }));

      const existingPhone = existingProfile.phone_number || '';
      const existingSmsOptIn = !!existingProfile.sms_opt_in;
      const existingOptOutSource = (existingProfile.sms_opt_out_source || '').toString().toLowerCase();
      setSmsOptOutSource(existingOptOutSource);

      setSmsProfile({
        phoneNumber: existingPhone,
        smsOptIn: existingSmsOptIn
      });

      // Keep the input as "new phone number" (empty by default). Avoid clobbering in-progress edits.
      setSmsSettings((prev) => ({
        ...prev,
        phoneInput: prev.phoneInput && prev.phoneInput !== existingPhone ? prev.phoneInput : '',
        smsOptIn: existingSmsOptIn,
        // Not stored yet; assume accepted if already opted in previously
        termsAccepted: existingSmsOptIn
      }));
    } catch (error) {
      console.error('Profile operation error:', error);
      // Don't set error state for 409 conflicts as we handle them
      if (error?.code !== 'PGRST116') {
        setError('Failed to load profile data');
      }
    } finally {
      profileRefreshGuardRef.current.inFlight = false;
      profileRefreshGuardRef.current.lastAt = Date.now();
    }
  }, [user]);

  const toastMessage = error || success;
  const toastSeverity = error ? 'error' : 'success';

  useEffect(() => {
    if (toastMessage) setToastOpen(true);
  }, [toastMessage]);

  const handleToastClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setToastOpen(false);
    setError('');
    setSuccess('');
  };

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // If the user opts out by texting STOP, reflect it in the UI when they return to this tab.
  useEffect(() => {
    if (!user) return;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchUserProfile();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, fetchUserProfile]);

  // Password validation check
  useEffect(() => {
    const newPassword = passwordData.newPassword;
    setPasswordValidation({
      hasMinLength: newPassword.length >= 8,
      hasUpperCase: /[A-Z]/.test(newPassword),
      hasLowerCase: /[a-z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
    });
  }, [passwordData.newPassword]);

  const isProductionHost = useMemo(() => {
    if (typeof window === 'undefined') return true;
    const host = (window.location?.hostname || '').toLowerCase();
    // Keep production strict to US-only (update if/when you add more prod domains)
    return host === 'pregoprep.com' || host.endsWith('.pregoprep.com');
  }, []);

  // Format E.164 US phone number to (XXX) XXX-XXXX display format
  const formatPhoneForDisplay = useCallback((e164) => {
    if (!e164) return '';
    // Extract digits only
    const digits = e164.replace(/\D/g, '');
    // For US numbers (+1XXXXXXXXXX), remove the leading 1
    if (digits.length === 11 && digits.startsWith('1')) {
      const tenDigits = digits.slice(1);
      return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
    }
    // For 10-digit US numbers (already without country code)
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // For non-US or invalid formats, return as-is
    return e164;
  }, []);

  // Format phone input as user types (US format: (XXX) XXX-XXXX)
  const formatPhoneInput = useCallback((input) => {
    if (!input) return '';
    // Remove all non-digits
    const digits = input.replace(/\D/g, '');
    // If 11 digits and starts with 1 (US country code), remove the leading 1
    const tenDigits = (digits.length === 11 && digits.startsWith('1'))
      ? digits.slice(1)
      : digits.slice(0, 10); // Otherwise limit to 10 digits

    if (tenDigits.length === 0) return '';
    if (tenDigits.length <= 3) return `(${tenDigits}`;
    if (tenDigits.length <= 6) return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3)}`;
    return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
  }, []);

  const normalizePhoneToE164 = useCallback((input) => {
    const raw = (input || '').trim();
    if (!raw) return null;

    // Allow explicit E.164 numbers like +918000000000
    if (raw.startsWith('+')) {
      const digits = raw.replace(/\D/g, '');
      // E.164 max is 15 digits (excluding +); use 8 as a practical minimum
      if (digits.length >= 8 && digits.length <= 15) return `+${digits}`;
      return null;
    }

    const digits = raw.replace(/\D/g, '');
    if (!digits) return null;

    // If user picked India, allow 10-digit local numbers without +91
    if (smsSettings.phoneCountry === 'IN') {
      if (digits.length === 10) return `+91${digits}`;
      if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
      return null;
    }

    // Israel: 9 digits without leading 0 (e.g. 501234567), or 10 digits with leading 0 (e.g. 0501234567)
    if (smsSettings.phoneCountry === 'IL') {
      if (digits.length === 9 && digits.startsWith('5')) return `+972${digits}`;
      if (digits.length === 10 && digits.startsWith('05')) return `+972${digits.slice(1)}`;
      if (digits.length === 12 && digits.startsWith('972')) return `+${digits}`;
      return null;
    }

    // US numbers: 10 digits (no country code) or 11 digits starting with 1
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;

    return null;
  }, [smsSettings.phoneCountry]);

  const normalizedSmsPhone = useMemo(() => {
    return normalizePhoneToE164(smsSettings.phoneInput);
  }, [smsSettings.phoneInput, normalizePhoneToE164]);

  const isSmsPhoneValid = useMemo(() => {
    return Boolean(normalizedSmsPhone);
  }, [normalizedSmsPhone]);

  const canSaveSms = useMemo(() => {
    if (smsLoading) return false;

    const isStopOptedOut = smsOptOutSource === 'stop' && smsProfile.smsOptIn === false;
    const hasTypedPhone = (smsSettings.phoneInput || '').trim().length > 0;
    const hasExistingPhone = Boolean(smsProfile.phoneNumber);

    // Opt-in path: require valid phone + SMS checkbox checked + Terms accepted
    if (smsSettings.smsOptIn) {
      // Carrier STOP blocks delivery; website opt-in cannot override.
      if (isStopOptedOut) return false;
      // If the user typed a new phone, it must be valid.
      if (hasTypedPhone) return Boolean(normalizedSmsPhone) && smsSettings.termsAccepted;
      // Otherwise, allow saving if there's already a stored phone number.
      return hasExistingPhone && smsSettings.termsAccepted;
    }

    // Phone-only path: allow saving phone number without opt-in
    // User can add or update phone number without checking opt-in boxes
    if (hasTypedPhone) {
      // If typing a new phone, it must be valid
      // Even if STOP'd, allow updating phone number
      return Boolean(normalizedSmsPhone);
    }

    // If STOP'd and no phone change, prevent saving (would incorrectly change 'stop' to 'profile')
    if (isStopOptedOut && !hasTypedPhone) {
      return false;
    }

    // If no typed phone but has existing phone, allow saving (to opt-out or keep current state)
    // But only if not STOP'd (STOP'd users should use START to re-enable)
    if (hasExistingPhone) {
      return true;
    }
    // No phone at all - can't save
    return false;
  }, [
    smsLoading,
    smsOptOutSource,
    smsSettings.smsOptIn,
    smsSettings.termsAccepted,
    smsSettings.phoneInput,
    normalizedSmsPhone,
    smsProfile.phoneNumber,
    smsProfile.smsOptIn,
  ]);

  const buildNextPhoneHistory = useCallback((currentHistory, previousPhone) => {
    const prev = (previousPhone || '').toString().trim();
    if (!prev) return currentHistory || [];

    const base = Array.isArray(currentHistory) ? currentHistory : [];

    // Avoid appending duplicates (common if user toggles back/forth).
    const alreadyThere = base.some((entry) => {
      if (!entry) return false;
      if (typeof entry === 'string') return entry === prev;
      if (typeof entry === 'object' && entry.phone) return entry.phone === prev;
      return false;
    });
    if (alreadyThere) return base;

    return [
      ...base,
      { phone: prev, archived_at: new Date().toISOString() },
    ];
  }, []);

  const handleSaveSmsSettings = async (e, options = {}) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSmsPhoneError('');
    setSmsLoading(true);

    try {
      if (!user) throw new Error('No user session found');

      const desiredOptIn = !!smsSettings.smsOptIn;
      const action = options?.action || 'save'; // "save" | "add_phone" | "update_phone" | "phone_only_add" | "phone_only_update" | "phone_delete"

      // Free users CAN opt in to SMS (they receive the welcome SMS).
      // Reminders and summary SMS are gated separately behind subscription.

      // If action is 'save' and user has typed a phone but hasn't opted in,
      // it means they want to save phone AND opt out (not just phone-only)
      const hasTypedPhone = (smsSettings.phoneInput || '').trim().length > 0;

      // Fetch current values fresh for correct transition logic
      const { data: currentProfile, error: currentError } = await supabase
        .from('profiles')
        .select('phone_number, phone_number_history, sms_opt_in, sms_opt_in_date, sms_opt_out_source')
        .eq('user_id', user.id)
        .maybeSingle();

      if (currentError) throw currentError;
      if (!currentProfile) throw new Error('Profile not found. Please refresh and try again.');

      const currentOptOutSource = (currentProfile.sms_opt_out_source || '').toString().toLowerCase();
      const isStopOptedOut = currentOptOutSource === 'stop' && currentProfile.sms_opt_in === false;

      // DELETE PHONE: remove phone number and force SMS opt-out.
      // - Ensures the UI checkboxes are unchecked
      // - Prevents "SMS enabled" without a reachable phone
      if (action === 'phone_delete') {
        const currentPhone = (currentProfile.phone_number || '').toString().trim();
        if (!currentPhone) throw new Error('No phone number on file to delete.');

        const nextHistory = buildNextPhoneHistory(currentProfile.phone_number_history, currentPhone);

        // If the user previously unsubscribed via STOP, preserve that source.
        // Deleting the phone number must NOT change 'stop' to 'profile', otherwise the
        // website could incorrectly allow re-enabling SMS without a START text.
        const preserveStopSource = currentOptOutSource === 'stop';
        const deleteOptOutSource = preserveStopSource ? 'stop' : 'profile';

        const updates = {
          updated_at: new Date(),
          phone_number: null,
          phone_number_history: nextHistory,
          // Force-disable SMS since there’s no phone number anymore
          sms_opt_in: false,
          sms_opt_out_date: new Date(),
          sms_opt_out_source: deleteOptOutSource,
          // Allow the Calculator banner to re-appear in the future after removal
          sms_prompt_dismissed_at: null,
          // DO NOT reset sms_prompt_seen_count - keep it to respect the 3-impression limit
          // Users should not see the popup 3 more times just because they deleted their phone
        };

        const { data, error } = await supabase
          .from('profiles')
          .update(updates)
          .eq('user_id', user.id)
          .select('phone_number, sms_opt_in, sms_opt_out_source')
          .single();

        if (error) throw error;

        // Cancel all pending/retrying reminders when phone number is deleted
        // This prevents reminders from being sent to a deleted phone number
        try {
          const { error: cancelErr } = await supabase
            .from('scheduled_reminders')
            .update({
              status: 'cancelled',
              cancelled_at: new Date().toISOString(),
            })
            .eq('user_id', user.id)
            .in('status', ['pending', 'retrying']);
          if (cancelErr) {
            console.warn('Failed to cancel pending reminders on phone deletion (non-blocking):', cancelErr);
          }
        } catch (cancelE) {
          console.warn('Failed to cancel pending reminders on phone deletion (non-blocking):', cancelE);
        }

        setSmsProfile({
          phoneNumber: data.phone_number || '',
          smsOptIn: !!data.sms_opt_in
        });
        setSmsOptOutSource((data?.sms_opt_out_source || '').toString().toLowerCase());

        setSmsSettings(prev => ({
          ...prev,
          phoneInput: '',
          smsOptIn: false,
          termsAccepted: false
        }));

        setSuccess('Phone number deleted.');
        setSmsLoading(false);
        return;
      }

      // PHONE-ONLY: allow updating the stored phone number without opting into SMS.
      // This must NOT change sms_opt_in and must NOT require Terms acceptance.
      // (Only used by explicit "Add Phone Number" and "Update Phone Number" buttons)
      if (action === 'phone_only_add' || action === 'phone_only_update') {
        const currentPhone = (currentProfile.phone_number || '').toString().trim();
        const typedRaw = (smsSettings.phoneInput || '').trim();
        const hasTypedPhone = typedRaw.length > 0;

        if (!hasTypedPhone) {
          const msg = 'Please enter a phone number.';
          setSmsPhoneError(msg);
          throw new Error(msg);
        }

        const e164 = normalizedSmsPhone;
        if (!e164) {
          const msg = isProductionHost
            ? 'Please enter a valid US phone number.'
            : 'Please enter a valid phone number. Use +<countrycode><number> (E.164) for non-US.';
          setSmsPhoneError(msg);
          throw new Error(msg);
        }

        if (action === 'phone_only_add' && currentPhone) {
          throw new Error('A phone number already exists. Use Update instead.');
        }
        if (action === 'phone_only_update' && !currentPhone) {
          throw new Error('No phone number on file yet. Use Add instead.');
        }
        if (currentPhone && e164 === currentPhone) {
          throw new Error('That phone number is already saved.');
        }

        const nextHistory =
          action === 'phone_only_update'
            ? buildNextPhoneHistory(currentProfile.phone_number_history, currentPhone)
            : currentProfile.phone_number_history;

        const updates = {
          updated_at: new Date(),
          phone_number: e164,
          // If the user adds/updates a phone number, allow the Calculator banner to re-appear
          // in the future if they remove the phone number.
          sms_prompt_dismissed_at: null,
          ...(action === 'phone_only_update' ? { phone_number_history: nextHistory } : {}),
        };

        const { data, error } = await supabase
          .from('profiles')
          .update(updates)
          .eq('user_id', user.id)
          .select('phone_number, sms_opt_in, sms_opt_out_source')
          .single();

        if (error) throw error;

        // Cancel all pending/retrying reminders when phone number changes
        // This prevents reminders from being sent to the old phone number
        try {
          const { error: cancelErr } = await supabase
            .from('scheduled_reminders')
            .update({
              status: 'cancelled',
              cancelled_at: new Date().toISOString(),
            })
            .eq('user_id', user.id)
            .in('status', ['pending', 'retrying']);
          if (cancelErr) {
            console.warn('Failed to cancel pending reminders on phone number change (non-blocking):', cancelErr);
          }
        } catch (cancelE) {
          console.warn('Failed to cancel pending reminders on phone number change (non-blocking):', cancelE);
        }

        setSmsProfile({
          phoneNumber: data.phone_number || '',
          smsOptIn: !!data.sms_opt_in
        });
        setSmsOptOutSource((data?.sms_opt_out_source || '').toString().toLowerCase());

        setSmsSettings(prev => ({
          ...prev,
          phoneInput: '',
          smsOptIn: !!data.sms_opt_in,
          termsAccepted: !!data.sms_opt_in
        }));

        setSuccess('Phone number saved.');
        setSmsLoading(false);
        return;
      }

      // Carrier compliance: If user opted out via STOP, website cannot re-enable SMS.
      // They must text START to the Twilio number.
      if (desiredOptIn && isStopOptedOut) {
        throw new Error(`You unsubscribed by texting STOP. To re-subscribe, text START to ${TWILIO_SUBSCRIBE_NUMBER}.`);
      }

      if (desiredOptIn) {
        if (!smsSettings.termsAccepted) {
          throw new Error('Please accept the Terms of Service & Privacy Policy to enable SMS reminders.');
        }

        const currentPhone = (currentProfile.phone_number || '').toString().trim();
        const typedRaw = (smsSettings.phoneInput || '').trim();
        const hasTypedPhone = typedRaw.length > 0;

        // Determine the phone we intend to save:
        // - if the user typed a phone, use/validate it
        // - otherwise, keep the current stored phone (if any)
        const e164 = hasTypedPhone ? normalizedSmsPhone : (currentPhone || null);

        // Enforce Add/Update button intent without breaking the normal "Save" flow.
        if (action === 'add_phone') {
          if (currentPhone) throw new Error('A phone number already exists. Use Update instead.');
          if (!hasTypedPhone) {
            const msg = 'Please enter a phone number to add.';
            setSmsPhoneError(msg);
            throw new Error(msg);
          }
        }
        if (action === 'update_phone') {
          if (!currentPhone) throw new Error('No phone number on file yet. Use Add instead.');
          if (!hasTypedPhone) {
            const msg = 'Please enter a new phone number to update.';
            setSmsPhoneError(msg);
            throw new Error(msg);
          }
        }

        // Validation: if the user typed a phone, it must normalize. If they didn't, require an existing phone.
        if (hasTypedPhone && !e164) {
          const msg = isProductionHost
            ? 'Please enter a valid US phone number.'
            : 'Please enter a valid phone number. Use +<countrycode><number> (E.164) for non-US.';
          setSmsPhoneError(msg);
          throw new Error(msg);
        }
        if (!hasTypedPhone && !e164) {
          const msg = 'Please add a phone number to enable SMS reminders.';
          setSmsPhoneError(msg);
          throw new Error(msg);
        }

        const phoneChanged = (currentPhone || '') !== e164;
        const optInChanged = !currentProfile.sms_opt_in;
        const shouldSendWelcome = optInChanged || phoneChanged;

        const nextHistory = phoneChanged
          ? buildNextPhoneHistory(currentProfile.phone_number_history, currentPhone)
          : currentProfile.phone_number_history;

        const updates = {
          updated_at: new Date(),
          phone_number: e164,
          sms_opt_in: true,
          sms_opt_out_date: null,
          sms_opt_out_source: null,
          // If phone is set, we don't need the "add phone" banner; but we clear dismissal so it can
          // appear again if the phone number is later removed.
          sms_prompt_dismissed_at: null,
          ...(phoneChanged ? { phone_number_history: nextHistory } : {}),
          ...(optInChanged || phoneChanged ? { sms_opt_in_date: new Date() } : {})
        };

        const { data, error } = await supabase
          .from('profiles')
          .update(updates)
          .eq('user_id', user.id)
          .select('*')
          .single();

        if (error) throw error;

        // Cancel all pending/retrying reminders when phone number changes during opt-in
        // This prevents reminders from being sent to the old phone number
        if (phoneChanged) {
          try {
            const { error: cancelErr } = await supabase
              .from('scheduled_reminders')
              .update({
                status: 'cancelled',
                cancelled_at: new Date().toISOString(),
              })
              .eq('user_id', user.id)
              .in('status', ['pending', 'retrying']);
            if (cancelErr) {
              console.warn('Failed to cancel pending reminders on phone number change (non-blocking):', cancelErr);
            }
          } catch (cancelE) {
            console.warn('Failed to cancel pending reminders on phone number change (non-blocking):', cancelE);
          }
        }

        setSmsProfile({
          phoneNumber: data.phone_number || '',
          smsOptIn: !!data.sms_opt_in
        });
        setSmsOptOutSource((data?.sms_opt_out_source || '').toString().toLowerCase());

        setSmsSettings(prev => ({
          ...prev,
          // Keep the input empty after a successful add/update; we show the saved number above the input.
          phoneInput: '',
          smsOptIn: !!data.sms_opt_in,
          termsAccepted: true
        }));

        setSuccess('SMS settings saved successfully.');

        // Best-effort welcome SMS (does not block saving)
        if (shouldSendWelcome) {
          try {
            // Use a direct fetch so we can control headers precisely.
            // (supabase.functions.invoke may send `Authorization: Bearer <anonKey>` when header merge order is unexpected.)
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData?.session?.access_token;
            if (!accessToken) throw new Error('Missing session access token');

            const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/+$/, '');
            const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
            if (!supabaseUrl || !anonKey) throw new Error('Missing Supabase env vars');

            const resp = await fetch(`${supabaseUrl}/functions/v1/send-welcome-sms`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                apikey: anonKey,
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({}),
            });

            if (!resp.ok) {
              const details = await resp.text().catch(() => '');
              let errorText = details || '';

              // Try to parse JSON error if available
              try {
                const errorJson = JSON.parse(errorText);
                errorText = errorJson.error || errorText;
              } catch {
                // Not JSON, use as-is
              }

              // Check if it's a 403 (not allowlisted) error - only show message in dev
              const isDev = process.env.NODE_ENV === 'development' ||
                supabaseUrl.includes('localhost') ||
                supabaseUrl.includes('127.0.0.1');

              if (resp.status === 402) {
                setUpgradeModal({ open: true });
              } else if (resp.status === 403 && (errorText.includes('not allowlisted') || errorText.includes('allowlist')) && isDev) {
                // Show error to user - SMS won't work until number is added to allowlist (dev only)
                setError('SMS notifications are enabled, but your phone number needs to be added to the allowlist for dev testing. Please contact support to have your number added.');
                // Keep sms_opt_in as true (user's preference is saved), but warn them it won't work
              } else if (resp.status === 409 && (errorText.toLowerCase().includes('opted out') || errorText.toLowerCase().includes('stop') || errorText.toLowerCase().includes('reply start'))) {
                // Twilio 21610 path – user previously replied STOP
                setError(
                  `You unsubscribed by texting STOP. Due to carrier/Twilio rules, we can't re-enable SMS from the website. To re-subscribe, text START to the Pre Go Prep number. If you deleted the thread, text START to ${TWILIO_SUBSCRIBE_NUMBER}.`
                );

                //Update profile to mark as STOP'd so the warning box appears
                try {
                  await supabase
                    .from('profiles')
                    .update({
                      sms_opt_out_source: 'stop',
                      sms_opt_out_date: new Date(),
                      sms_opt_in: false, // Also disable opt-in since SMS won't work
                    })
                    .eq('user_id', user.id);

                  setSmsProfile(prev => ({
                    ...prev,
                    smsOptIn: false
                  }));
                  setSmsOptOutSource('stop');
                  setSmsSettings(prev => ({
                    ...prev,
                    smsOptIn: false,
                    termsAccepted: false
                  }));

                  // Also refresh profile data to ensure consistency
                  await fetchUserProfile();
                } catch (updateErr) {
                  console.error('Failed to update STOP status:', updateErr);
                }
              } else {
                // For other errors or in production, just log as warning (non-blocking)
                console.warn('Welcome SMS failed (non-blocking):', `Status ${resp.status}: ${errorText}`);
              }
            } else {
              // Success - welcome SMS sent
            }
          } catch (welcomeErr) {
            // Catch any network errors or other exceptions
            console.warn('Welcome SMS failed (non-blocking):', welcomeErr);
          }
        }
        return;
      }

      // Opt-out path: user wants to opt out (checkboxes unchecked)
      // This can include saving/updating phone number if one was typed
      const currentPhone = (currentProfile.phone_number || '').toString().trim();
      const e164 = hasTypedPhone ? normalizedSmsPhone : null;

      // Validate phone if user typed one
      if (hasTypedPhone && !e164) {
        const msg = isProductionHost
          ? 'Please enter a valid US phone number.'
          : 'Please enter a valid phone number. Use +<countrycode><number> (E.164) for non-US.';
        setSmsPhoneError(msg);
        throw new Error(msg);
      }

      // Determine the phone number to save
      const phoneToSave = e164 || currentPhone || null;
      const phoneChanged = currentPhone && e164 && e164 !== currentPhone;

      const nextHistory = phoneChanged && currentPhone
        ? buildNextPhoneHistory(currentProfile.phone_number_history, currentPhone)
        : currentProfile.phone_number_history;

      // CRITICAL: If user previously opted out via STOP, preserve that source.
      // Don't allow website actions to change 'stop' to 'profile' - only START can re-enable.
      const preserveStopSource = currentOptOutSource === 'stop';
      const optOutSource = preserveStopSource ? 'stop' : 'profile';

      const updates = {
        updated_at: new Date(),
        sms_opt_in: false,
        sms_opt_out_date: new Date(),
        sms_opt_out_source: optOutSource,
        // If user typed a phone number, save/update it
        ...(phoneToSave ? { phone_number: phoneToSave } : {}),
        ...(phoneChanged ? { phone_number_history: nextHistory } : {}),
        // If phone is set, we don't need the "add phone" banner; but we clear dismissal so it can
        // appear again if the phone number is later removed.
        ...(phoneToSave ? { sms_prompt_dismissed_at: null } : {})
      };

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', user.id)
        .select('*')
        .single();

      if (error) throw error;

      // Cancel any pending/retrying SMS reminders so they are not sent after opt-out
      try {
        const { error: cancelErr } = await supabase
          .from('scheduled_reminders')
          .update({
            status: 'cancelled',
            cancelled_at: new Date().toISOString(),
          })
          .eq('user_id', user.id)
          .in('status', ['pending', 'retrying']);
        if (cancelErr) {
          console.warn('Failed to cancel pending reminders on opt-out (non-blocking):', cancelErr);
        }
      } catch (cancelE) {
        console.warn('Failed to cancel pending reminders on opt-out (non-blocking):', cancelE);
      }

      setSmsProfile({
        phoneNumber: data.phone_number || '',
        smsOptIn: !!data.sms_opt_in
      });
      setSmsOptOutSource((data?.sms_opt_out_source || '').toString().toLowerCase());

      setSmsSettings(prev => ({
        ...prev,
        phoneInput: '',
        smsOptIn: false,
        termsAccepted: false
      }));

      setSuccess(phoneToSave
        ? 'Phone number saved and SMS reminders disabled.'
        : 'SMS reminders disabled.');
    } catch (err) {
      console.error('Error saving SMS settings:', err);
      // Friendly handling for unique phone constraint violation
      // Postgres unique violation code: 23505
      const errCode = err?.code || err?.error?.code;
      const errMsg = (err?.message || err?.error?.message || '').toString();
      const errDetails = (err?.details || err?.error?.details || '').toString();
      const uniquePhoneHit =
        errCode === '23505' ||
        errMsg.includes('profiles_phone_number_unique') ||
        errDetails.includes('profiles_phone_number_unique') ||
        errMsg.toLowerCase().includes('duplicate key value') ||
        errMsg.toLowerCase().includes('unique constraint');

      if (uniquePhoneHit) {
        const msg = 'This phone number is already used by another account.';
        setSmsPhoneError(msg);
        setError(msg);
      } else {
        setError(err.message || 'Failed to save SMS settings');
      }
    } finally {
      setSmsLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select()
        .eq('user_id', user.id)
        .maybeSingle();

      if (checkError) throw checkError;

      const updates = {
        updated_at: new Date()
      };

      const trimmedFirst = (profileData.newFirstName || '').trim();
      const trimmedLast = (profileData.newLastName || '').trim();

      // Only update fields that have been explicitly provided, so empty inputs
      // do not wipe the existing name (same behavior as email update).
      if (trimmedFirst) {
        updates.first_name = trimmedFirst;
      }
      if (trimmedLast) {
        updates.last_name = trimmedLast;
      }

      let query = supabase.from('profiles');

      if (existingProfile) {
        query = query.update(updates).eq('user_id', user.id);
      } else {
        query = query.insert({ ...updates, user_id: user.id });
      }

      const { data, error } = await query.select().single();

      if (error) throw error;

      setSuccess('Profile updated successfully');

      setProfileData(prev => ({
        ...prev,
        firstName: data.first_name || prev.firstName || '',
        lastName: data.last_name || prev.lastName || '',
        newFirstName: '',
        newLastName: ''
      }));
    } catch (error) {
      setError('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      if (!profileData.newEmail || !/\S+@\S+\.\S+/.test(profileData.newEmail)) {
        throw new Error('Please enter a valid email address');
      }

      // Trigger Supabase's standard email-change confirmation flow. Supabase will email a
      // confirmation link to the NEW address (and, because double_confirm_changes is enabled
      // in config, a notification to the OLD address). The change does not take effect until
      // the user clicks the link in the new email — this prevents account takeover via a
      // leaked password and prevents typos from silently rerouting account/billing emails.
      const newEmail = profileData.newEmail;
      const { error } = await supabase.auth.updateUser(
        { email: newEmail },
        { emailRedirectTo: `${window.location.origin}/auth/callback` }
      );

      if (error) throw error;

      setSuccess(`A confirmation link has been sent to ${newEmail}. Click it from that inbox to complete the change. Your current email stays active until you confirm.`);
      setProfileData(prev => ({ ...prev, newEmail: '' }));
    } catch (error) {
      console.error('Error updating email:', error);
      setError(error.message || 'Failed to update email');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('New passwords do not match');
      }

      if (!Object.values(passwordValidation).every(Boolean)) {
        throw new Error('Please meet all password requirements');
      }

      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      setSuccess('Password updated successfully');
      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswords({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false
      });
    } catch (error) {
      console.error('Error updating password:', error);
      setError(error.message || 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const passwordRequirements = [
    { text: 'Minimum 8 characters', check: passwordValidation.hasMinLength },
    { text: 'At least one uppercase letter', check: passwordValidation.hasUpperCase },
    { text: 'At least one lowercase letter', check: passwordValidation.hasLowerCase },
    { text: 'At least one number', check: passwordValidation.hasNumber },
    { text: 'At least one special character (!@#$%^&*(),.?":{}|<>)', check: passwordValidation.hasSpecialChar }
  ];

  const commonStyles = {
    input: {
      width: '100%',
      padding: '8px 10px',
      borderRadius: '6px',
      border: '1px solid #ced4da',
      fontSize: '0.95rem',
      height: '36px',
      transition: 'border-color 0.2s',
      '&:focus': {
        borderColor: brandColors.primary,
        outline: 'none'
      }
    },
    button: {
      padding: '10px 20px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '16px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      '&:disabled': {
        opacity: 0.7,
        cursor: 'not-allowed'
      }
    },
    section: {
      marginBottom: '30px',
      padding: '20px',
      backgroundColor: brandColors.white,
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    }
  };

  return (
    <div style={{
      maxWidth: '800px',
      margin: `${marginTop} auto 0`,
      padding: '0 20px',
      position: 'relative'
    }}>
      <Snackbar
        open={toastOpen && Boolean(toastMessage)}
        autoHideDuration={toastSeverity === 'success' ? 4000 : null}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleToastClose}
          severity={toastSeverity}
          variant="filled"
          sx={{
            width: '100%',
            alignItems: 'center',            // vertically centers icon + text + close button
            '& .MuiAlert-message': {         // message container
              display: 'flex',
              alignItems: 'center',
              padding: 0,
            },
            '& .MuiAlert-action': {          // close button container
              alignItems: 'center',
              paddingTop: 0,
            },
          }} >
          {toastMessage}
        </Alert>
      </Snackbar>
      <h1 style={{
        margin: '1rem 0 15px 0',
        color: brandColors.primary,
        fontSize: '2.5rem',  // Increased from 1.8rem to 2.5rem
        textAlign: 'center',
        fontWeight: '600'    // Added to ensure the larger text has good presence
      }}>
        Pre Go Prep Profile Settings
      </h1>

      <div style={{
        backgroundColor: brandColors.white,
        borderRadius: '8px',
        border: `1px solid ${brandColors.lightBlue}`,
        padding: '15px',    // Reduced padding
        marginBottom: '15px'
      }}>
        {/* Personal Information Section */}
        <div style={commonStyles.section}>
          <h2 style={{
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: brandColors.primary
          }}>
            <FaUser /> Personal Information
          </h2>
          {/* Display current name if it exists */}
          {(profileData.firstName || profileData.lastName) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '15px',
              color: brandColors.primary,
              flexWrap: 'wrap',
              wordBreak: 'break-word',
              maxWidth: '100%'
            }}>
              <strong>Current Name:</strong>
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordWrap: 'break-word',
                maxWidth: '100%'
              }}>
                {`${profileData.firstName} ${profileData.lastName}`.trim()}
              </span>
            </div>
          )}
          <form onSubmit={handleUpdateProfile}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '20px',
              width: '100%'
            }}>
              <div style={{ width: '100%' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: brandColors.primary }}>First Name</label>
                <input
                  type="text"
                  value={profileData.newFirstName}
                  onChange={(e) => setProfileData(prev => ({ ...prev, newFirstName: e.target.value }))}
                  style={commonStyles.input}
                  placeholder="Enter first name"
                />
              </div>
              <div style={{ width: '100%' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: brandColors.primary }}>Last Name</label>
                <input
                  type="text"
                  value={profileData.newLastName}
                  onChange={(e) => setProfileData(prev => ({ ...prev, newLastName: e.target.value }))}
                  style={commonStyles.input}
                  placeholder="Enter last name"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...commonStyles.button,
                backgroundColor: brandColors.primary,
                color: brandColors.white,
                '&:hover': {
                  backgroundColor: '#1e3266'
                }
              }}
            >
              {isLoading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>
        </div>

        {/* Email Section */}
        <div style={commonStyles.section}>
          <h2 style={{
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: brandColors.primary
          }}>
            <FaEnvelope /> Email Settings
          </h2>
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '15px',
              color: brandColors.primary,
              flexWrap: 'wrap',
              wordBreak: 'break-all',
              maxWidth: '100%'
            }}>
              <strong>Current Email:</strong>
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordWrap: 'break-word',
                maxWidth: '100%'
              }}>
                {user?.email}
              </span>
            </div>
          </div>
          <form onSubmit={handleUpdateEmail}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: brandColors.primary }}>New Email Address</label>
              <input
                type="email"
                value={profileData.newEmail}
                onChange={(e) => setProfileData(prev => ({ ...prev, newEmail: e.target.value }))}
                style={commonStyles.input}
                placeholder="Enter new email address"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...commonStyles.button,
                backgroundColor: brandColors.primary,
                color: brandColors.white,
                '&:hover': {
                  backgroundColor: '#1e3266'
                }
              }}
            >
              {isLoading ? 'Updating...' : 'Update Email'}
            </button>
          </form>
        </div>

        {/* Subscription & Billing Section */}
        <div id="subscription-billing">
          <SubscriptionSection onUpgrade={() => setUpgradeModal({ open: true })} />
        </div>

        {/* SMS Notifications Section */}
        <div id="sms-notifications" style={commonStyles.section}>
          {(() => {
            if (!fromPricingTrial || bannerDismissed) return null;
            const hasPhoneNow = !!(smsProfile.phoneNumber && smsProfile.phoneNumber.trim());
            if (hasPhoneNow) return null;
            return (
              <Alert
                severity="error"
                onClose={() => setBannerDismissed(true)}
                sx={{ mb: 2 }}
              >
                Please add your phone number in the SMS Notifications section below before subscribing.
              </Alert>
            );
          })()}

          <h2 style={{
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: brandColors.primary
          }}>
            <FaMobileAlt /> SMS Notifications
          </h2>

          <div style={{ marginBottom: '16px', color: brandColors.primary }}>
            <strong>Status:</strong>{' '}
            <span style={{ color: smsProfile.smsOptIn ? '#198754' : '#6c757d' }}>
              {smsProfile.smsOptIn ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          {smsProfile.smsOptIn === false && smsOptOutSource === 'stop' && (
            <div
              style={{
                backgroundColor: '#fff7ed',
                border: '1px solid #fed7aa',
                borderLeft: `6px solid ${brandColors.secondary}`,
                borderRadius: '12px',
                padding: '12px 14px',
                marginBottom: '16px',
                color: '#7c2d12',
                lineHeight: 1.35,
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: '6px' }}>
                You unsubscribed by texting STOP
              </div>
              <div style={{ fontSize: '0.95rem' }}>
                You unsubscribed by texting STOP. Due to carrier/Twilio rules, we can't re-enable SMS from the website.
                To re-subscribe, text START to the Pre Go Prep number. If you deleted the thread, text START to{' '}
                <strong>{TWILIO_SUBSCRIBE_NUMBER}</strong>.
              </div>
            </div>
          )}

          {smsProfile.smsOptIn === false && smsOptOutSource === 'profile' && (
            <div
              style={{
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderLeft: `6px solid ${brandColors.primary}`,
                borderRadius: '12px',
                padding: '12px 14px',
                marginBottom: '16px',
                color: '#1e3a8a',
                lineHeight: 1.35,
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: '6px' }}>
                SMS reminders are turned off
              </div>
              <div style={{ fontSize: '0.95rem' }}>
                You unsubscribed via the website (Profile). To start receiving texts again, enable SMS Reminders and save
                your settings.
              </div>
            </div>
          )}

          <form onSubmit={handleSaveSmsSettings}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '15px',
                color: brandColors.primary,
                flexWrap: 'wrap',
                wordBreak: 'break-all',
                maxWidth: '100%'
              }}>
                <strong>Current Phone:</strong>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordWrap: 'break-word',
                  maxWidth: '100%'
                }}>
                  {smsProfile.phoneNumber ? formatPhoneForDisplay(smsProfile.phoneNumber) : 'None'}
                </span>
              </div>

              <label style={{ display: 'block', marginBottom: '8px', color: brandColors.primary }}>
                Phone Number (US only)
              </label>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch', flexWrap: 'wrap' }}>
                <select
                  value={smsSettings.phoneCountry}
                  onChange={(e) => setSmsSettings((prev) => ({ ...prev, phoneCountry: e.target.value }))}
                  aria-label="Country code"
                  style={{
                    ...commonStyles.input,
                    width: '190px',
                    maxWidth: '100%',
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                    marginBottom: 0,
                  }}
                >
                  <option value="US">United States (+1)</option>
                  <option value="IN">India (+91)</option>
                  <option value="IL">Israel (+972)</option>
                </select>

                <input
                  type="tel"
                  value={smsSettings.phoneInput}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    // Format US phone numbers as user types: (XXX) XXX-XXXX
                    const formattedValue = smsSettings.phoneCountry === 'US'
                      ? formatPhoneInput(newValue)
                      : newValue;

                    // If user is updating the phone number (has existing phone and typing new one),
                    // uncheck the checkboxes so they need to opt-in again with the new number
                    const hasExistingPhone = Boolean(smsProfile.phoneNumber);
                    const isTypingNewNumber = formattedValue.trim().length > 0;
                    // Uncheck checkboxes when user starts typing a new number to update existing one
                    const shouldUncheck = hasExistingPhone && isTypingNewNumber;

                    setSmsSettings(prev => ({
                      ...prev,
                      phoneInput: formattedValue,
                      // Uncheck checkboxes when updating phone number (but not when adding first time)
                      smsOptIn: shouldUncheck ? false : prev.smsOptIn,
                      termsAccepted: shouldUncheck ? false : prev.termsAccepted
                    }));
                    setSmsPhoneError('');
                  }}
                  style={{
                    ...commonStyles.input,
                    flex: 1,
                    minWidth: '240px',
                    borderColor: smsPhoneError ? '#dc3545' : (smsSettings.phoneInput && isSmsPhoneValid ? '#198754' : '#ced4da')
                  }}
                  placeholder={
                    smsSettings.phoneCountry === 'US' ? '(555) 555-5555'
                    : smsSettings.phoneCountry === 'IL' ? '054 123 4567'
                    : '80000 00000'
                  }
                  disabled={smsLoading}
                />
              </div>

              {/* {!isProductionHost && (
                <div style={{ marginTop: '6px', fontSize: '0.85rem', color: '#6c757d' }}>
                  Dev/Preview note: you can still use E.164 numbers (ex: +918000000000) for testing. Production is US-only.
                </div>
              )} */}
              {/* <div style={{ marginTop: '6px', fontSize: '0.85rem', color: smsPhoneError ? '#dc3545' : '#6c757d' }}>
                {smsPhoneError
                  ? smsPhoneError
                  : (smsSettings.phoneInput && isSmsPhoneValid
                    ? `Looks good — we’ll save it as ${normalizedSmsPhone}.`
                    : (smsProfile.phoneNumber
                      ? (isProductionHost
                        ? 'US (+1) numbers only. Leave blank to keep your current number, or enter a new US number to update it.'
                        : 'Leave blank to keep your current number, or enter a new number to update it.')
                      : (isProductionHost
                        ? 'US (+1) numbers only. To enable SMS: enter a valid US mobile number.'
                        : 'Non-production: E.164 numbers allowed for testing (ex: +918000000000). Production requires a US (+1) number.')))}
              </div> */}
            </div>

            <div style={{ display: 'grid', gap: '12px', marginBottom: '18px' }}>
              <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={smsSettings.smsOptIn}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSmsSettings(prev => ({
                      ...prev,
                      smsOptIn: checked,
                      termsAccepted: checked ? prev.termsAccepted : false
                    }));
                    setSmsPhoneError('');
                  }}
                  disabled={smsLoading || (smsProfile.smsOptIn === false && smsOptOutSource === 'stop')}
                  style={{ marginTop: '3px' }}
                />
                <div>
                  <div style={{ fontWeight: '600', color: brandColors.primary }}>
                    Enable SMS Reminders
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6c757d', lineHeight: 1.3 }}>
                    By checking this box, I agree to receive automated text messages from Pre Go Prep at the phone number I have provided related to race day preparation and timing reminders.
                  </div>
                </div>
              </label>

              <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={smsSettings.termsAccepted}
                  onChange={(e) => setSmsSettings(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                  disabled={smsLoading || !smsSettings.smsOptIn || (smsProfile.smsOptIn === false && smsOptOutSource === 'stop')}
                  style={{ marginTop: '3px' }}
                />
                <div>
                  <div style={{ fontWeight: '600', color: brandColors.primary }}>
                    I accept the Terms of Service &amp; Privacy Policy
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6c757d', lineHeight: 1.3 }}>
                    By providing your phone number you agree to receive automated messages from Pre Go Prep related to race day preparation and/or timing reminders. Consent is not a condition of purchase. Pre Go Prep does not send marketing messages. Message &amp; data rates may apply. Message frequency varies. Reply HELP for help or STOP to opt out.
                    {' '}
                    <span>
                      (
                      <Link to="/terms-of-service" style={{ color: brandColors.primary, textDecoration: 'none' }}>
                        Terms
                      </Link>
                      {' '}|{' '}
                      <Link to="/privacy" style={{ color: brandColors.primary, textDecoration: 'none' }}>
                        Privacy
                      </Link>
                      )
                    </span>
                  </div>
                </div>
              </label>
            </div>

            <div className="smsButtonsRow">
              <button
                type="submit"
                disabled={!canSaveSms}
                style={{
                  ...commonStyles.button,
                  backgroundColor: canSaveSms ? brandColors.primary : '#9aa7c7',
                  color: brandColors.white,
                  opacity: canSaveSms ? 1 : 0.75,
                  cursor: canSaveSms ? 'pointer' : 'not-allowed',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: '#1e3266'
                  }
                }}
              >
                {smsLoading ? 'Saving...' : 'Save SMS Settings'}
              </button>

              {/* Phone delete removed — users can only update their phone number */}
            </div>
            {!canSaveSms && smsSettings.smsOptIn && (
              <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#6c757d' }}>
                To enable SMS: enter a valid phone number and accept Terms &amp; Privacy.
              </div>
            )}
          </form>
        </div>

        {/* Password Section */}
        <div style={commonStyles.section}>
          <h2 style={{
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: brandColors.primary
          }}>
            <FaLock /> Password Settings
          </h2>
          <button
            onClick={() => setIsChangingPassword(!isChangingPassword)}
            style={{
              ...commonStyles.button,
              backgroundColor: isChangingPassword ? brandColors.secondary : brandColors.primary,
              color: isChangingPassword ? brandColors.primary : brandColors.white,
              marginBottom: '20px',
              '&:hover': {
                backgroundColor: isChangingPassword ? '#c4ab6c' : '#1e3266'
              }
            }}
          >
            {isChangingPassword ? 'Cancel' : 'Change Password'}
          </button>

          {isChangingPassword && (
            <form onSubmit={handlePasswordChange}>
              <div style={{
                marginBottom: '20px',
                maxWidth: '500px',
                margin: '0 auto'
              }}>
                {['currentPassword', 'newPassword', 'confirmPassword'].map((field) => (
                  <div key={field} style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      color: brandColors.primary,
                      fontSize: '0.9rem'
                    }}>
                      {field === 'currentPassword' ? 'Current Password' :
                        field === 'newPassword' ? 'New Password' : 'Confirm New Password'}
                    </label>
                    <div style={{
                      position: 'relative',
                      width: '100%'
                    }}>
                      <input
                        type={showPasswords[field] ? "text" : "password"}
                        name={field}
                        value={passwordData[field]}
                        onChange={handlePasswordInputChange}
                        style={{
                          ...commonStyles.input,
                          paddingRight: '40px',
                          height: '36px',
                          fontSize: '0.95rem'
                        }}
                        placeholder={`Enter ${field === 'currentPassword' ? 'current' : field === 'newPassword' ? 'new' : 'confirmed'} password`}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility(field)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '5px',
                          color: brandColors.primary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '30px',
                          height: '30px'
                        }}
                        aria-label={showPasswords[field] ? "Hide password" : "Show password"}
                      >
                        {showPasswords[field] ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Password Requirements */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '10px', fontSize: '1rem', color: brandColors.primary }}>
                  Password Requirements
                </h3>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {passwordRequirements.map((req, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: req.check ? '#198754' : '#dc3545',
                        fontSize: '0.9rem'
                      }}
                    >
                      {req.check ? <FaCheck /> : <FaTimes />}
                      {req.text}
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !Object.values(passwordValidation).every(Boolean)}
                style={{
                  ...commonStyles.button,
                  backgroundColor: brandColors.primary,
                  color: brandColors.white,
                  '&:hover': {
                    backgroundColor: '#1e3266'
                  }
                }}
              >
                {isLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Delete Phone Number Confirmation Dialog */}
      <UpgradeModal
        open={upgradeModal.open}
        reason={upgradeModal.reason}
        onClose={() => setUpgradeModal({ open: false })}
      />
    </div>
  );
}

export default Profile; 